/**
 * Outfit quiz "Outfit for an occasion" — Figma Quizzes › `Outfit quiz · desktop`
 * 3229:18076 / `mobile` 3229:104944. Built like the Gift quiz: Welcome → Gender (only if
 * Personal data has none) → Occasion (Other opens a text field) → Details (dress code +
 * weather, Custom opens a field) → Budget → Note → Loading → Result (three outfits) →
 * Open outfits = the Playground pick-one screen. Occasion and budget are asked every time
 * and not stored; styles, brands and colors come from the profile.
 */
import type { AiraActionBody, AiraAudience, AiraProposedOutfit } from '@/features/aira/api/types'

export type OutfitQuizStep =
  | 'welcome'
  | 'gender'
  | 'occasion'
  | 'details'
  | 'budget'
  | 'note'
  | 'loading'
  | 'result'
  | 'error'

/** 02 Occasion — the old event list merged with dinner, cocktail and wedding guest. */
export const OUTFIT_QUIZ_OCCASIONS = [
  { value: 'casual_day', icon: 'everyday' },
  { value: 'work_event', icon: 'workwear' },
  { value: 'study', icon: 'study' },
  { value: 'date', icon: 'date' },
  { value: 'dinner', icon: 'dinner' },
  { value: 'party', icon: 'party' },
  { value: 'cocktail', icon: 'cocktail' },
  { value: 'wedding_guest', icon: 'wedding' },
  { value: 'holiday', icon: 'holiday' },
  { value: 'travel', icon: 'vacation' },
  { value: 'other', icon: 'edit' },
] as const

export type OutfitQuizOccasion = (typeof OUTFIT_QUIZ_OCCASIONS)[number]['value']

/** 04 Details — two single-choice blocks; Custom opens a field and clears the rest (R171). */
export const OUTFIT_QUIZ_DRESS_CODES = ['casual', 'smart_casual', 'business', 'cocktail', 'black_tie', 'custom'] as const
export const OUTFIT_QUIZ_WEATHER = ['warm', 'mild', 'cool', 'cold', 'not_sure', 'custom'] as const

export type OutfitQuizDressCode = (typeof OUTFIT_QUIZ_DRESS_CODES)[number]
export type OutfitQuizWeather = (typeof OUTFIT_QUIZ_WEATHER)[number]

/** 06 Budget — whole outfit, USD placeholders of the macket. */
export const OUTFIT_QUIZ_BUDGETS = [
  { value: 'under_500', min: null, max: 500 },
  { value: '500_1500', min: 500, max: 1500 },
  { value: '1500_3000', min: 1500, max: 3000 },
  { value: '3000_6000', min: 3000, max: 6000 },
  { value: '6000_plus', min: 6000, max: null },
  { value: 'not_sure', min: null, max: null },
] as const

export type OutfitQuizBudget = (typeof OUTFIT_QUIZ_BUDGETS)[number]['value']

export interface OutfitQuizAnswers {
  occasion: OutfitQuizOccasion | null
  occasionText: string
  dressCode: OutfitQuizDressCode | null
  dressCodeText: string
  weather: OutfitQuizWeather | null
  weatherText: string
  budget: OutfitQuizBudget | null
  note: string
}

export function emptyOutfitQuizAnswers(): OutfitQuizAnswers {
  return {
    occasion: null,
    occasionText: '',
    dressCode: null,
    dressCodeText: '',
    weather: null,
    weatherText: '',
    budget: null,
    note: '',
  }
}

export function outfitQuizSteps(askGender: boolean): OutfitQuizStep[] {
  return ['welcome', ...(askGender ? (['gender'] as const) : []), 'occasion', 'details', 'budget', 'note']
}

export function outfitQuizProgress(steps: OutfitQuizStep[], step: OutfitQuizStep): number {
  const index = steps.indexOf(step)
  if (index < 0) return step === 'welcome' ? 0 : 1
  return index / steps.length
}

/** Tapping the chosen option again clears it (Custom closes its field). */
export function toggleOutfitQuizChoice<T extends string>(current: T | null, value: T): T | null {
  return current === value ? null : value
}

const CURRENCY_SCALE: Record<string, number> = { USD: 1, EUR: 1, BYN: 3, RUB: 100 }

export function formatOutfitQuizBudget(
  value: OutfitQuizBudget,
  currency: string,
  labels: { under: string; notSure: string },
): string {
  const budget = OUTFIT_QUIZ_BUDGETS.find((item) => item.value === value)
  if (!budget || value === 'not_sure') return labels.notSure
  const scale = CURRENCY_SCALE[currency] ?? 1
  const money = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount * scale)
  const plain = (amount: number) => new Intl.NumberFormat('en-US').format(amount * scale)
  if (budget.min === null && budget.max !== null) return `${labels.under} ${money(budget.max)}`
  if (budget.min !== null && budget.max === null) return `${money(budget.min)}+`
  return `${money(budget.min!)}–${plain(budget.max!)}`
}

/** Personal data gender → the stylist's audience. */
export function outfitQuizAudience(gender: string | null | undefined): AiraAudience | null {
  const key = gender?.toLowerCase()
  if (key === 'woman' || key === 'female') return 'women'
  if (key === 'man' || key === 'male') return 'men'
  return null
}

/**
 * `POST /aira/actions` — `shuffle_outfit`, three drafts (R133) from the catalogue. The
 * envelope has no dress code / weather / budget fields (BE-154): the occasion goes as the
 * shopper's words, the rest in `answers` with the labels shown in the quiz.
 */
export function outfitQuizActionBody(
  answers: OutfitQuizAnswers,
  labels: { occasion: string; dressCode: string; weather: string; budget: string },
  audience: AiraAudience | null,
  lang: string,
  clientActionId: string,
): AiraActionBody {
  const brief: Record<string, string> = {}
  if (labels.dressCode) brief.dress_code = labels.dressCode
  if (labels.weather) brief.weather = labels.weather
  if (labels.budget) brief.budget = labels.budget
  if (answers.note.trim()) brief.note = answers.note.trim()
  return {
    action: 'shuffle_outfit',
    candidates: 3,
    clientActionId,
    lang,
    context: {
      surface: 'playground',
      canvasItems: [],
      occasion: labels.occasion || null,
      audience,
      itemSource: 'catalog',
      answers: brief,
    },
  }
}

/** The three outfits of the answer; a degraded turn or no proposal is an error (10). */
export function outfitQuizProposed(response: {
  status?: string
  actions?: Array<{ type: string; outfits?: AiraProposedOutfit[] }>
}): AiraProposedOutfit[] | null {
  if (response.status && response.status !== 'ok') return null
  const proposal = response.actions?.find((action) => action.type === 'propose_outfits')
  const outfits = (proposal?.outfits ?? []).filter((outfit) => outfit.items?.length)
  return outfits.length ? outfits : null
}

/**
 * Figma `Look` (205×190): six framed pieces, positions in percent, in item order.
 * Each frame has 5px air around the picture.
 */
export const OUTFIT_QUIZ_LOOK_SLOTS = [
  { left: 9.34, top: 3.78, width: 35.03, height: 50.4 },
  { left: 50.01, top: 6.93, width: 30.65, height: 44.1 },
  { left: 35.81, top: 35.59, width: 28.31, height: 53.7 },
  { left: 68.4, top: 35.8, width: 26.27, height: 28.35 },
  { left: 19.85, top: 62.83, width: 19.85, height: 21.42 },
  { left: 56.14, top: 68.5, width: 19.85, height: 21.42 },
] as const
